import { System } from '@ecs/System';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { PointDefense } from '@components/PointDefense';
import { Energy } from '@components/Energy';
import { Tag } from '@components/Tag';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Collider } from '@components/Collider';
import { BulletTrail } from '@components/BulletTrail';
import { SoundManager, SoundType } from '@audio/SoundManager';
import { Damage } from '@components/Damage';

export class PointDefenseSystem extends System {
  update(_deltaTime: number): void {
    const currentTime = Date.now();
    const defenders = this.getEntitiesWithComponents(Position, PointDefense, Energy, Tag);
    // Point defense engages anything guided or explosive: torpedoes and mines
    const torpedoes = this.getEntitiesWithComponents(Position, Tag).filter(e => {
      const tag = e.getComponent(Tag)!.value;
      return tag === 'torpedo' || tag === 'mine';
    });

    for (const defender of defenders) {
      const tag = defender.getComponent(Tag)!;
      if (tag.value !== 'player') continue;

      const position = defender.getComponent(Position)!;
      const pointDefense = defender.getComponent(PointDefense)!;
      const energy = defender.getComponent(Energy)!;

      // Check if we're in a burst
      const inBurst = pointDefense.bulletsFiredinBurst < pointDefense.bulletsPerBurst && 
                     currentTime - pointDefense.lastBurstStart < pointDefense.bulletsPerBurst * pointDefense.bulletCooldown;

      if (inBurst) {
        // Continue firing burst if enough time has passed since last bullet
        if (currentTime - pointDefense.lastBulletTime >= pointDefense.bulletCooldown && 
            energy.current >= pointDefense.energyCost) {
          
          // Find nearest torpedo within range
          let nearestTorpedo = this.findNearestTorpedo(position, pointDefense.range, torpedoes);
          
          if (nearestTorpedo) {
            const baseAngle = this.calculateInterceptAngle(position, nearestTorpedo, pointDefense.bulletSpeed);
            
            // Add spread to the bullet
            const spread = (Math.random() - 0.5) * pointDefense.bulletSpread;
            const finalAngle = baseAngle + spread;
            
            // Create point defense bullet
            this.createPointDefenseBullet(position, finalAngle, pointDefense);
            
            // Update burst tracking
            energy.current -= pointDefense.energyCost;
            pointDefense.lastBulletTime = currentTime;
            pointDefense.bulletsFiredinBurst++;
            
            // Play sound (slightly reduced volume for rapid fire)
            SoundManager.getInstance().playSound(SoundType.POINT_DEFENSE, 0.4);
            
            // Small muzzle flash only for every other bullet
            if (pointDefense.bulletsFiredinBurst % 2 === 0) {
              this.createMuzzleFlash(position, finalAngle);
            }
          }
        }
      } else {
        // Check if we can start a new burst
        if (currentTime - pointDefense.lastBurstStart >= pointDefense.burstCooldown && torpedoes.length > 0) {
          // Find target for new burst
          let nearestTorpedo = this.findNearestTorpedo(position, pointDefense.range, torpedoes);
          
          if (nearestTorpedo && energy.current >= pointDefense.energyCost * pointDefense.bulletsPerBurst * 0.5) {
            // Start new burst
            pointDefense.lastBurstStart = currentTime;
            pointDefense.bulletsFiredinBurst = 0;
            pointDefense.lastBulletTime = currentTime - pointDefense.bulletCooldown; // Allow immediate first shot
          }
        }
      }
    }
  }

  private findNearestTorpedo(position: Position, range: number, torpedoes: Entity[]): { entity: Entity, position: Position, distance: number } | null {
    let nearestTorpedo: { entity: Entity, position: Position, distance: number } | null = null;
    let minDistance = range;

    for (const torpedo of torpedoes) {
      const torpedoPos = torpedo.getComponent(Position)!;
      const dx = torpedoPos.x - position.x;
      const dy = torpedoPos.y - position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < minDistance) {
        minDistance = distance;
        nearestTorpedo = { entity: torpedo, position: torpedoPos, distance };
      }
    }

    return nearestTorpedo;
  }

  private calculateInterceptAngle(position: Position, target: { entity: Entity, position: Position, distance: number }, bulletSpeed: number): number {
    const torpedoVel = target.entity.getComponent(Velocity);
    let targetX = target.position.x;
    let targetY = target.position.y;

    if (torpedoVel) {
      // Much less accurate lead calculation
      const timeToHit = target.distance / bulletSpeed;
      const leadFactor = 0.1 + Math.random() * 0.3; // Much worse lead calculation (10-40% accuracy)
      targetX += Math.cos(torpedoVel.angle) * torpedoVel.speed * timeToHit * leadFactor;
      targetY += Math.sin(torpedoVel.angle) * torpedoVel.speed * timeToHit * leadFactor;
    }

    return Math.atan2(targetY - position.y, targetX - position.x);
  }

  private createPointDefenseBullet(position: Position, angle: number, pointDefense: PointDefense): void {
    const bullet = new Entity()
      .addComponent(new Position(
        position.x + Math.cos(angle) * 20,
        position.y + Math.sin(angle) * 20
      ))
      .addComponent(new Velocity(angle, pointDefense.bulletSpeed))
      .addComponent(new Renderable('bolt', pointDefense.bulletColor, 1.2, undefined, 0, pointDefense.bulletColor, 3)) // Small tracer
      .addComponent(new Lifetime(30, 30)) // Shorter lifetime
      .addComponent(new Collider(2, 'pointDefenseBullet', ['enemyBullet'])) // Very small hitbox
      .addComponent(new Tag('bullet'))
      .addComponent(new BulletTrail())
      .addComponent(new Damage(pointDefense.damage));

    this.world.addEntity(bullet);
  }

  private createMuzzleFlash(position: Position, angle: number): void {
    // Very small, brief flash for rapid fire effect
    const flash = new Entity()
      .addComponent(new Position(
        position.x + Math.cos(angle) * 18,
        position.y + Math.sin(angle) * 18
      ))
      .addComponent(new Velocity(0, 0))
      .addComponent(new Renderable('circle', '#ffffcc', 4)) // Smaller, dimmer flash
      .addComponent(new Lifetime(2, 2)) // Very brief
      .addComponent(new Tag('particle'));

    this.world.addEntity(flash);
  }
}